/**
 * FlexBox Component
 *
 * Reusable flex container with common patterns used throughout the dashboard.
 * Reduces repetitive flexbox styling code.
 */
import React from "react";
import { Box, BoxProps } from "@mui/material";

interface FlexBoxProps extends BoxProps {
  /** Flex direction */
  direction?: "row" | "column";
  /** Align items */
  align?: "flex-start" | "center" | "flex-end" | "stretch" | "baseline";
  /** Justify content */
  justify?:
    | "flex-start"
    | "center"
    | "flex-end"
    | "space-between"
    | "space-around"
    | "space-evenly";
  /** Gap between items */
  gap?: number;
  /** Flex wrap */
  wrap?: "nowrap" | "wrap" | "wrap-reverse";
  /** Full width */
  fullWidth?: boolean;
}

/**
 * FlexBox - Reusable flex container
 *
 * @example
 * ```tsx
 * <FlexBox align="center" gap={2}>
 *   <Icon />
 *   <Typography>Label</Typography>
 * </FlexBox>
 * ```
 */
export const FlexBox: React.FC<FlexBoxProps> = ({
  direction = "row",
  align = "center",
  justify = "flex-start",
  gap = 1,
  wrap = "nowrap",
  fullWidth = false,
  children,
  sx,
  ...props
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: direction,
      alignItems: align,
      justifyContent: justify,
      gap,
      flexWrap: wrap,
      width: fullWidth ? "100%" : undefined,
      ...sx,
    }}
    {...props}
  >
    {children}
  </Box>
);

// ================================
// Common Flex Pattern Variants
// ================================

/** Flex row with centered items and gap */
export const FlexRow: React.FC<Omit<FlexBoxProps, "direction">> = (props) => (
  <FlexBox direction="row" {...props} />
);

/** Flex column with gap */
export const FlexColumn: React.FC<Omit<FlexBoxProps, "direction">> = (
  props
) => <FlexBox direction="column" {...props} />;

/** Flex row with space-between */
export const FlexBetween: React.FC<Omit<FlexBoxProps, "justify">> = (props) => (
  <FlexBox justify="space-between" {...props} />
);

/** Flex row with centered items */
export const FlexCenter: React.FC<Omit<FlexBoxProps, "justify" | "align">> = (
  props
) => <FlexBox align="center" justify="center" {...props} />;

export default FlexBox;
